import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useStaticQuery, graphql } from "gatsby";
import { Tween, ScrollTrigger } from "react-gsap";

import SmartText from "../../utils/TextHandler";
import SmartColour from "../../utils/ColourHandler";
import ImageHandler from "../../utils/ImageHandler";
import CardNews from "../Cards/CardNews";
import CardButton from "../Cards/CardButton";

const SectionNews = (props) => {
  const { doc, location } = props;

  const country = useSelector((state) => state.global.country);

  const data = useStaticQuery(graphql`
    query NewsFeaturedAllQuery {
      default: allStoryblokEntry(
        filter: {
          field_component: { eq: "news_post" }
          lang: { eq: "default" }
        }
        sort: {
          fields: [field_featured_boolean, field_date_string]
          order: [DESC, DESC]
        }
        limit: 3
      ) {
        nodes {
          full_slug
          field_date_string
          content
          parent_id
        }
      }

      de: allStoryblokEntry(
        filter: { field_component: { eq: "news_post" }, lang: { eq: "de" } }
        sort: {
          fields: [field_featured_boolean, field_date_string]
          order: [DESC, DESC]
        }
        limit: 3
      ) {
        nodes {
          full_slug
          field_date_string
          content
          parent_id
        }
      }

      es: allStoryblokEntry(
        filter: { field_component: { eq: "news_post" }, lang: { eq: "es" } }
        sort: {
          fields: [field_featured_boolean, field_date_string]
          order: [DESC, DESC]
        }
        limit: 3
      ) {
        nodes {
          full_slug
          field_date_string
          content
          parent_id
        }
      }

      fr: allStoryblokEntry(
        filter: { field_component: { eq: "news_post" }, lang: { eq: "fr" } }
        sort: {
          fields: [field_featured_boolean, field_date_string]
          order: [DESC, DESC]
        }
        limit: 3
      ) {
        nodes {
          full_slug
          field_date_string
          content
          parent_id
        }
      }

      it: allStoryblokEntry(
        filter: { field_component: { eq: "news_post" }, lang: { eq: "it" } }
        sort: {
          fields: [field_featured_boolean, field_date_string]
          order: [DESC, DESC]
        }
        limit: 3
      ) {
        nodes {
          full_slug
          field_date_string
          content
          parent_id
        }
      }

      zh: allStoryblokEntry(
        filter: { field_component: { eq: "news_post" }, lang: { eq: "zh" } }
        sort: {
          fields: [field_featured_boolean, field_date_string]
          order: [DESC, DESC]
        }
        limit: 3
      ) {
        nodes {
          full_slug
          field_date_string
          content
          parent_id
        }
      }
    }
  `);

  let items = data.default.nodes;
  if (country === "de") {
    items = data.de.nodes;
  } else if (country === "es") {
    items = data.es.nodes;
  } else if (country === "fr") {
    items = data.fr.nodes;
  } else if (country === "it") {
    items = data.it.nodes;
  } else if (country === "zh") {
    items = data.zh.nodes;
  }

  const colour = SmartColour(doc.colour);

  return (
    <section
      id={doc.id && doc.id}
      className={`brand-section py-5 py-lg-10 d-flex flex-column justify-content-center align-items-center brand-background-${colour.background} brand-text-${colour.text} brand-background-cover`}
      style={
        doc.background && doc.background.filename
          ? {
              backgroundImage: `url(${ImageHandler(
                doc.background.filename,
                "1920x1080"
              )})`,
            }
          : {}
      }
    >
      <ScrollTrigger start="-100px center" end="0 top">
        <div className="container">
          {doc.title && (
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                <Tween
                  from={{ opacity: 0, y: "30px" }}
                  duration={1}
                  ease="Power4.easeOut"
                >
                  <h1
                    className={`brand-font mb-5 brand-text-dirty brand-text-${colour.headline} brand-font-responsive`}
                  >
                    {doc.title}
                  </h1>
                </Tween>
                {doc.body && <SmartText>{doc.body}</SmartText>}
              </div>
            </div>
          )}
          <div className="row gy-4 justify-content-start mb-4 mb-md-5">
            {items.map((node, index) => {
              const delay = 0.2 * index;
              return (
                <Tween
                  key={index}
                  from={{ opacity: 0, y: "30px" }}
                  duration={1}
                  delay={delay}
                  ease="Power4.easeOut"
                >
                  <div className="col-12 col-md-6 col-lg-4 h-100" key={index}>
                    <CardNews data={node} location={location} />
                  </div>
                </Tween>
              );
            })}
          </div>
          <div className="row gy-4 justify-content-center">
            <div className="col-12 col-md-6 col-lg-4 h-100">
              <CardButton
                text={doc.button_presskit}
                link={doc.link_presskit}
                imageSrc="https://a.storyblok.com/f/156499/92x92/9c3ea41785/icon-arrow-down.png"
                imageAlt="Icon Arrow Down"
                imageStyle={{ height: "24px" }}
              />
            </div>
            <div className="col-12 col-md-6 col-lg-4 h-100">
              <CardButton
                text={doc.button_news}
                link={doc.link_news}
                imageSrc="https://a.storyblok.com/f/156499/170x92/9a77605090/icon-arrow-right.png"
                imageAlt="Icon Arrow Right"
                imageStyle={{ height: "24px" }}
              />
            </div>
          </div>
        </div>
      </ScrollTrigger>
    </section>
  );
};

export default SectionNews;

SectionNews.propTypes = {
  doc: PropTypes.object,
};

SectionNews.defaultProps = {
  doc: null,
};
